function leaf(value) {
    return { type: 'leaf', value };
}
function mean(values, indices) {
    if (indices.length === 0)
        return 0;
    let sum = 0;
    for (const i of indices)
        sum += values[i];
    return sum / indices.length;
}
/**
 * Scans every feature for the SSE-minimizing split point, using prefix sums
 * over each feature's sorted order so each candidate threshold is O(1) to
 * score rather than re-summing both sides from scratch.
 */
function findBestSplit(X, y, indices, minSamplesLeaf) {
    const dims = X[0]?.length ?? 0;
    let best = null;
    for (let j = 0; j < dims; j++) {
        const sorted = [...indices].sort((a, b) => X[a][j] - X[b][j]);
        const n = sorted.length;
        if (n < 2 * minSamplesLeaf)
            continue;
        const prefixSum = new Array(n + 1).fill(0);
        const prefixSumSq = new Array(n + 1).fill(0);
        for (let k = 0; k < n; k++) {
            const yi = y[sorted[k]];
            prefixSum[k + 1] = prefixSum[k] + yi;
            prefixSumSq[k + 1] = prefixSumSq[k] + yi * yi;
        }
        const totalSum = prefixSum[n];
        const totalSumSq = prefixSumSq[n];
        for (let k = minSamplesLeaf; k <= n - minSamplesLeaf; k++) {
            // No real threshold sits between two samples with an identical value.
            if (X[sorted[k - 1]][j] === X[sorted[k]][j])
                continue;
            const leftSum = prefixSum[k];
            const leftSumSq = prefixSumSq[k];
            const rightSum = totalSum - leftSum;
            const rightSumSq = totalSumSq - leftSumSq;
            const leftSse = leftSumSq - (leftSum * leftSum) / k;
            const rightSse = rightSumSq - (rightSum * rightSum) / (n - k);
            const sse = leftSse + rightSse;
            if (!best || sse < best.sse) {
                best = {
                    featureIndex: j,
                    threshold: (X[sorted[k - 1]][j] + X[sorted[k]][j]) / 2,
                    leftIndices: sorted.slice(0, k),
                    rightIndices: sorted.slice(k),
                    sse,
                };
            }
        }
    }
    return best;
}
function buildNode(X, y, indices, depth, options) {
    if (depth >= options.maxDepth || indices.length < 2 * options.minSamplesLeaf) {
        return leaf(mean(y, indices));
    }
    const split = findBestSplit(X, y, indices, options.minSamplesLeaf);
    if (!split)
        return leaf(mean(y, indices));
    return {
        type: 'split',
        featureIndex: split.featureIndex,
        threshold: split.threshold,
        left: buildNode(X, y, split.leftIndices, depth + 1, options),
        right: buildNode(X, y, split.rightIndices, depth + 1, options),
    };
}
/** Fits one regression tree to `y` (any real-valued target, e.g. residuals) on `X`. */
export function fitRegressionTree(X, y, options) {
    if (X.length !== y.length || X.length === 0) {
        throw new Error('fitRegressionTree: X and y must be the same non-zero length.');
    }
    const indices = X.map((_, i) => i);
    return buildNode(X, y, indices, 0, options);
}
/**
 * Descends to the leaf `x` lands in and returns that leaf object itself (not
 * a copy) — callers that need to identify which training rows share a leaf
 * (e.g. the boosting loop's Newton-step refinement) rely on this identity.
 */
export function findLeaf(node, x) {
    let current = node;
    while (current.type === 'split') {
        current = x[current.featureIndex] <= current.threshold ? current.left : current.right;
    }
    return current;
}
export function predictTree(node, x) {
    return findLeaf(node, x).value;
}
//# sourceMappingURL=tree.js.map